// src/pages/content-management/helpdesk/document-tab.tsx
import { useState, type Dispatch, type SetStateAction } from 'react'
import { type TFunction } from 'i18next'
import { Download, Eye, MoreVertical, Trash2 } from 'lucide-react'

import { DataTable, Filters, Pagination, showErrorToast, showSuccessToast, type Column } from '@/components/common'
import {
    Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Badge, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui'
import type { Document } from '@/types/document'
import { logError } from '@/utils/logger'

interface DocumentTabProps {
    paginatedDocuments: Document[]
    selectedItems: Set<string>
    setSelectedItems: Dispatch<SetStateAction<Set<string>>>
    onDeleteDocumentClick: (doc: Document | Document[]) => void
    t: TFunction
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    pageSize: number
    onPageSizeChange: (size: number) => void
    fileInputRef: React.RefObject<HTMLInputElement>
    onAddDocument: (document: Document) => Promise<void>
}

export function DocumentTab({
    paginatedDocuments,
    selectedItems,
    setSelectedItems,
    onDeleteDocumentClick,
    t,
    currentPage,
    totalPages,
    onPageChange,
    pageSize,
    onPageSizeChange,
    fileInputRef,
    onAddDocument,
}: DocumentTabProps) {
    const [searchQuery, setSearchQuery] = useState("")
    const [selectedType, setSelectedType] = useState<string>("all")
    const [selectedStatus, setSelectedStatus] = useState<string>("all")
    const [sortConfig, setSortConfig] = useState<{ key: keyof Document | null; direction: 'asc' | 'desc' }>({
        key: null,
        direction: 'asc'
    });

    // Get unique document types from the documents
    const documentTypes = Array.from(new Set(paginatedDocuments.map(doc => doc.type)))
        .map(type => ({
            value: type,
            label: type
        }))

    // Get unique statuses from the documents
    const statusOptions = [
        { value: "processed", label: t('helpdesk.admin.processed') },
        { value: "pending", label: t('helpdesk.admin.pending') },
        { value: "error", label: t('helpdesk.admin.error') }
    ]

    // Filter documents based on search query, type, status and sort config
    const filteredDocuments = paginatedDocuments.filter(doc => {
        const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesType = selectedType === "all" || doc.type === selectedType
        const matchesStatus = selectedStatus === "all" || doc.status === selectedStatus
        return matchesSearch && matchesType && matchesStatus
    }).sort((a, b) => {
        if (!sortConfig.key) return 0;

        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc'
                ? aValue - bValue
                : bValue - aValue;
        }

        return 0;
    })

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B'
        else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
        else return (bytes / 1048576).toFixed(1) + ' MB'
    }

    // Bulk delete
    const handleBulkDelete = async () => {
        try {
            const documentsToDelete = Array.from(selectedItems)
                .map(id => paginatedDocuments.find(d => d.id === id))
                .filter((doc): doc is Document => doc !== undefined)

            await onDeleteDocumentClick(documentsToDelete)
            setSelectedItems(new Set())
            //showSuccessToast(t("success.document.bulkDeleteSuccess", { count: selectedItems.size }))
        } catch (error) {
            logError('Error bulk deleting documents:', error as Error)
            showErrorToast(t("error.document.bulkDeleteError", { count: selectedItems.size }))
        }
    }

    // Handlers for selection
    const handleSelectItem = (document: Document) => {
        const newSelected = new Set(selectedItems);
        if (newSelected.has(document.id)) {
            newSelected.delete(document.id);
        } else {
            newSelected.add(document.id);
        }
        setSelectedItems(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedItems.size === paginatedDocuments.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(
                new Set(paginatedDocuments.map((document) => document.id))
            );
        }
    };

    const handleViewDocument = (doc: Document) => {
        console.log('View document:', doc) // Placeholder
    }

    const handleDownloadDocument = (doc: Document) => {
        console.log('Download document:', doc) // Placeholder
    }

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (files && files.length > 0) {
            try {
                for (let i = 0; i < files.length; i++) {
                    const file = files[i]
                    const newDocument: Document = {
                        id: String(Date.now() + i), // Generate a unique ID
                        name: file.name,
                        type: file.type.split('/')[1].toUpperCase(),
                        size: file.size,
                        uploadDate: new Date(),
                        status: 'pending'
                    }
                    await onAddDocument(newDocument)
                }
                showSuccessToast(t('success.document.uploadSuccess', { count: files.length }))
            } catch (error) {
                logError('Error uploading documents:', error as Error)
                showErrorToast(t('error.document.uploadError'))
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const documentColumns: Column<Document>[] = [
        {
            key: 'name',
            label: t('helpdesk.admin.documentName'),
            sortable: true,
        },
        {
            key: 'type',
            label: t('helpdesk.admin.documentType'),
            sortable: true,
        },
        {
            key: 'size',
            label: t('helpdesk.admin.size'),
            sortable: true,
            render: (_, row) => formatFileSize(row.size),
        },
        {
            key: 'uploadDate',
            label: t('helpdesk.admin.uploadDate'),
            sortable: true,
            render: (_, row) => row.uploadDate.toLocaleDateString(),
        },
        {
            key: 'status',
            label: t('helpdesk.admin.status'),
            sortable: true,
            render: (_, row) => (
                <Badge
                    variant={
                        row.status === 'processed' ? 'default' :
                            row.status === 'pending' ? 'default' : 'destructive'
                    }
                >
                    {row.status === 'processed'
                        ? t('helpdesk.admin.processed')
                        : row.status === 'pending'
                            ? t('helpdesk.admin.pending')
                            : t('helpdesk.admin.error')
                    }
                </Badge>
            ),
        },
        {
            key: 'actions',
            label: t('helpdesk.admin.actions'),
            render: (_, doc) => (
                <div className="text-right">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={() => handleViewDocument(doc)}
                                className="flex items-center cursor-pointer"
                            >
                                <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                {t('helpdesk.admin.view')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => handleDownloadDocument(doc)}
                                className="flex items-center cursor-pointer"
                            >
                                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                {t('helpdesk.admin.download')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => onDeleteDocumentClick(doc)}
                                className="flex items-center cursor-pointer text-red-500 hover:text-red-500 focus:text-red-500"
                            >
                                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                {t('helpdesk.admin.delete', 'Verwijderen')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ),
        },
    ]

    return (
        <div className="flex flex-col gap-6">

            <Filters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filters={[
                    {
                        name: "documentType",
                        value: selectedType,
                        onChange: setSelectedType,
                        options: documentTypes
                    },
                    {
                        name: "status",
                        value: selectedStatus,
                        onChange: setSelectedStatus,
                        options: statusOptions
                    }
                ]}
                selectedCount={selectedItems.size}
                onBulkDelete={selectedItems.size > 0 ? handleBulkDelete : undefined}
            />

            <Card>
                <CardHeader>
                    <CardTitle>{t('helpdesk.admin.knowledgeBase')}</CardTitle>
                    <CardDescription>{t('helpdesk.admin.knowledgeBaseDescription')}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    <DataTable
                        columns={documentColumns}
                        data={filteredDocuments}
                        getId={(row) => row.id}
                        selectedItems={selectedItems}
                        onSelectItem={handleSelectItem}
                        onSelectAll={handleSelectAll}
                        sortConfig={sortConfig}
                        onSort={(key) => {
                            setSortConfig(prev => ({
                                key: key as keyof Document,
                                direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
                            }));
                        }}
                        className="p-0 w-full [&_tr_td:first-child]:pl-6 [&_tr_td:last-child]:pr-6 [&_tr_th:first-child]:!pl-6 [&_tr_th:last-child]:!pr-6 [&_tr_td]:py-3 [&_tr_th]:py-5"
                    />
                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={onPageChange}
                            pageSize={pageSize}
                            onPageSizeChange={onPageSizeChange}
                        />
                    )}
                </CardContent>
            </Card>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                multiple
            />
        </div>
    )
} 